import { create } from 'zustand';

import type { GlobalAssistantEntityType } from './global-ai-assistant-api';
import type { GlobalAiConversationSummary } from './global-ai-assistant-conversation';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import type { ReasoningTrace } from './reasoning-trace';

/**
 * Conversation state (transcript + summary + entity scope + tracking id) in a
 * vanilla zustand store instead of provider `useState`.
 *
 * WHY: the turn-end autosave fires synchronously inside the engine's
 * `isTurnRunning` flip — the same sync chain as the turn's final transcript
 * append. React state committed on the provider is render-lagged at that
 * moment, which previously forced the save through a deferred-consume dance
 * (pending ref + post-commit effect + macrotask fallback + a turn-start id
 * guard against pairing an old transcript with a new id). Store writes are
 * synchronous and the whole conversation lives in ONE store, so a turn-end
 * reader gets a current, mutually-consistent snapshot with none of that.
 *
 * The provider still subscribes (via `useStore`) and re-exposes the same
 * context API, so components are unaffected.
 *
 * Reset semantics: the provider resets this store as it mounts (lazy
 * initializer), mirroring the old per-provider-instance `useState` lifetime —
 * fresh state per provider mount, and no cross-test leakage through the
 * module-global store.
 */
type GlobalAiConversationStateStore = {
  transcript: GlobalAiTranscriptMessage[];
  conversationSummary: GlobalAiConversationSummary | null;
  sessionEntityScope: GlobalAssistantEntityType | null;
  /**
   * Client-generated conversation id: analytics correlation AND the persisted
   * conversation id from the first autosave on. Regenerated on New Chat;
   * replaced on hydration.
   */
  trackingId: string;

  /** Append; a no-op when a message with the same id is already present. */
  appendMessage: (message: GlobalAiTranscriptMessage) => void;
  updateAssistantMessageText: (id: string, text: string) => void;
  updateReasoningTrace: (id: string, trace: ReasoningTrace) => void;
  /** Mark a nav_confirm's one-shot continuation resume as consumed. */
  markNavConfirmResolved: (id: string) => void;
  setTranscript: (next: GlobalAiTranscriptMessage[]) => void;
  setConversationSummary: (
    next:
      | GlobalAiConversationSummary
      | null
      | ((
          prev: GlobalAiConversationSummary | null
        ) => GlobalAiConversationSummary | null)
  ) => void;
  setSessionEntityScope: (value: GlobalAssistantEntityType | null) => void;
  setTrackingId: (value: string) => void;
  /**
   * Install a whole persisted conversation in ONE write so every store field
   * advances atomically — a subscriber can never observe the new transcript
   * paired with the pre-hydration trackingId. Used by hydration.
   */
  setConversationPayload: (payload: {
    transcript: GlobalAiTranscriptMessage[];
    sessionEntityScope: GlobalAssistantEntityType | null;
    conversationSummary: GlobalAiConversationSummary | null;
    trackingId: string;
  }) => void;
  /** Fresh-session reset (provider mount / tests). */
  resetConversationState: (trackingId: string) => void;
};

export const useGlobalAiConversationStateStore =
  create<GlobalAiConversationStateStore>((set) => ({
    transcript: [],
    conversationSummary: null,
    sessionEntityScope: null,
    // Placeholder until the provider's mount reset stamps a real uuid —
    // crypto at module scope would run at import time in jsdom setups.
    trackingId: '',

    appendMessage: (message) =>
      set((state) =>
        state.transcript.some((existing) => existing.id === message.id)
          ? state
          : { transcript: [...state.transcript, message] }
      ),
    updateAssistantMessageText: (id, text) =>
      set((state) => ({
        transcript: state.transcript.map((message) =>
          message.id === id && 'text' in message
            ? { ...message, text }
            : message
        ),
      })),
    updateReasoningTrace: (id, trace) =>
      set((state) => ({
        transcript: state.transcript.map((message) =>
          message.id === id &&
          message.role === 'assistant' &&
          message.kind === 'reasoning_trace'
            ? { ...message, trace }
            : message
        ),
      })),
    markNavConfirmResolved: (id) =>
      set((state) => ({
        transcript: state.transcript.map((message) =>
          message.id === id &&
          message.role === 'assistant' &&
          message.kind === 'nav_confirm'
            ? { ...message, resolved: true }
            : message
        ),
      })),
    setTranscript: (next) => set({ transcript: next }),
    setConversationSummary: (next) =>
      set((state) => ({
        conversationSummary:
          typeof next === 'function' ? next(state.conversationSummary) : next,
      })),
    setSessionEntityScope: (value) => set({ sessionEntityScope: value }),
    setTrackingId: (value) => set({ trackingId: value }),
    setConversationPayload: ({
      transcript,
      sessionEntityScope,
      conversationSummary,
      trackingId,
    }) =>
      set({
        transcript,
        sessionEntityScope,
        conversationSummary,
        trackingId,
      }),
    resetConversationState: (trackingId) =>
      set({
        transcript: [],
        conversationSummary: null,
        sessionEntityScope: null,
        trackingId,
      }),
  }));
